import React, { useEffect, useRef, useCallback } from 'react';
import { Box, Container, Chip } from '@mui/material';

// ─── constants ────────────────────────────────────────────────────────────────
const CELL        = 48;
const MAX_NODES   = 12;   // concurrent animated cells
const SPAWN_MS    = 1800; // ms between new cell spawns
const DUR_MIN     = 5000;
const DUR_MAX     = 9000;

// Brand palette  (#9FE870 bg, #163300 text)
const C_GRID      = 'rgba(22,51,0,0.07)';
const C_GLOW      = [22, 120, 30];   // deep emerald glow
const C_FILL      = [22,  80, 20];   // active cell fill tint
const C_LINE      = 'rgba(22,100,30,{a})';
const C_MOUSE     = 'rgba(22,51,0,{a})';

// ─── math helpers ─────────────────────────────────────────────────────────────
const lerp   = (a, b, t) => a + (b - a) * t;
const clamp  = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
// smooth sine bell: 0→1→0 over [0,1]
const bell   = (t) => Math.pow(Math.sin(clamp(t, 0, 1) * Math.PI), 1.6);
// ease-in-out cubic
const eio    = (t) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

// ─── canvas hook ──────────────────────────────────────────────────────────────
function useGridCanvas(canvasRef, mouseRef) {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    let raf, cols = 0, rows = 0;
    // node: { col, row, born, duration }
    const nodes = [];
    let lastSpawn = 0;

    // ── sizing ──────────────────────────────────────────────────────────────
    const resize = () => {
      canvas.width  = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      cols = Math.ceil(canvas.width  / CELL) + 1;
      rows = Math.ceil(canvas.height / CELL) + 1;
    };
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    resize();

    // ── node management ─────────────────────────────────────────────────────
    const occupied = () => new Set(nodes.map(n => `${n.col},${n.row}`));

    const spawnNode = (now, delayOffset = 0) => {
      if (!cols || !rows) return;
      const taken = occupied();
      // try up to 12 times to find a free cell
      for (let attempt = 0; attempt < 12; attempt++) {
        const col = Math.floor(Math.random() * cols);
        const row = Math.floor(Math.random() * rows);
        if (taken.has(`${col},${row}`)) continue;
        nodes.push({
          col, row,
          born:     now - delayOffset,
          duration: lerp(DUR_MIN, DUR_MAX, Math.random()),
        });
        return;
      }
    };

    // seed staggered initial nodes
    const now0 = performance.now();
    const seedCount = Math.min(MAX_NODES, 8);
    for (let i = 0; i < seedCount; i++) {
      spawnNode(now0, Math.random() * 6000); // stagger up to 6 s back
    }

    // ── draw loop ────────────────────────────────────────────────────────────
    let last = performance.now();

    const draw = (now) => {
      raf = requestAnimationFrame(draw);
      last = now;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // 1. grid lines
      ctx.strokeStyle = C_GRID;
      ctx.lineWidth   = 1;
      for (let c = 0; c <= cols; c++) {
        ctx.beginPath();
        ctx.moveTo(c * CELL, 0);
        ctx.lineTo(c * CELL, canvas.height);
        ctx.stroke();
      }
      for (let r = 0; r <= rows; r++) {
        ctx.beginPath();
        ctx.moveTo(0, r * CELL);
        ctx.lineTo(canvas.width, r * CELL);
        ctx.stroke();
      }

      // 2. mouse proximity field
      const { x: mx, y: my } = mouseRef.current;
      if (mx !== null) {
        const mc = Math.floor(mx / CELL);
        const mr = Math.floor(my / CELL);
        for (let dc = -3; dc <= 3; dc++) {
          for (let dr = -3; dr <= 3; dr++) {
            const dist = Math.sqrt(dc * dc + dr * dr);
            if (dist > 3.2) continue;
            const a = (1 - dist / 3.2) * 0.10;
            ctx.fillStyle = C_MOUSE.replace('{a}', a.toFixed(3));
            ctx.fillRect((mc + dc) * CELL + 1, (mr + dr) * CELL + 1, CELL - 2, CELL - 2);
          }
        }
      }

      // 3. animated nodes
      const alive = [];
      for (const node of nodes) {
        const age = now - node.born;
        const t   = age / node.duration;

        if (t >= 1) continue; // expired — drop it
        alive.push(node);

        const p  = bell(t);          // 0 → 1 → 0 smooth bell
        const px = node.col * CELL;
        const py = node.row * CELL;
        const cx = px + CELL / 2;
        const cy = py + CELL / 2;

        const scale = 1 + p * 0.08;
        const lift  = -p * 5;

        ctx.save();
        ctx.translate(cx, cy + lift);
        ctx.scale(scale, scale);

        // glow shadow
        ctx.shadowColor = `rgba(${C_GLOW[0]},${C_GLOW[1]},${C_GLOW[2]},${(p * 0.55).toFixed(3)})`;
        ctx.shadowBlur  = p * 18;

        // cell fill — tinted emerald
        ctx.fillStyle = `rgba(${C_FILL[0]},${C_FILL[1]},${C_FILL[2]},${(p * 0.18).toFixed(3)})`;
        ctx.fillRect(-CELL / 2 + 1, -CELL / 2 + 1, CELL - 2, CELL - 2);

        // border highlight
        ctx.shadowBlur  = 0;
        ctx.strokeStyle = `rgba(${C_FILL[0]},${C_FILL[1] + 20},${C_FILL[2]},${(p * 0.40).toFixed(3)})`;
        ctx.lineWidth   = 1;
        ctx.strokeRect(-CELL / 2 + 1, -CELL / 2 + 1, CELL - 2, CELL - 2);

        ctx.restore();
      }

      // 4. connecting lines between nearby peak nodes
      const peaks = alive.filter(n => bell((now - n.born) / n.duration) > 0.55);
      for (let i = 0; i < peaks.length; i++) {
        for (let j = i + 1; j < peaks.length; j++) {
          const a = peaks[i], b = peaks[j];
          const ddx = a.col - b.col, ddy = a.row - b.row;
          if (ddx * ddx + ddy * ddy > 16) continue; // ≤ 4 cells apart
          const pa = bell((now - a.born) / a.duration);
          const pb = bell((now - b.born) / b.duration);
          const lineA = Math.min(pa, pb) * 0.20;
          ctx.beginPath();
          ctx.moveTo(a.col * CELL + CELL / 2, a.row * CELL + CELL / 2);
          ctx.lineTo(b.col * CELL + CELL / 2, b.row * CELL + CELL / 2);
          ctx.strokeStyle = C_LINE.replace('{a}', lineA.toFixed(3));
          ctx.lineWidth   = 0.8;
          ctx.stroke();
        }
      }

      // 5. prune expired + spawn new
      nodes.length = 0;
      nodes.push(...alive);

      if (nodes.length < MAX_NODES && now - lastSpawn > SPAWN_MS) {
        spawnNode(now);
        lastSpawn = now;
      }
    };

    raf = requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, [canvasRef, mouseRef]);
}

// ─── smooth parallax helper ───────────────────────────────────────────────────
function useSmoothParallax(parallaxRef) {
  const targetRef = useRef({ x: 0, y: 0 });
  const currentRef = useRef({ x: 0, y: 0 });
  const rafRef = useRef(null);

  const startLoop = useCallback(() => {
    if (rafRef.current) return;
    const loop = () => {
      const t = currentRef.current;
      const g = targetRef.current;
      t.x = lerp(t.x, g.x, 0.06);
      t.y = lerp(t.y, g.y, 0.06);
      if (parallaxRef.current) {
        parallaxRef.current.style.transform = `translate(${t.x.toFixed(2)}px,${t.y.toFixed(2)}px)`;
      }
      const done = Math.abs(t.x - g.x) < 0.01 && Math.abs(t.y - g.y) < 0.01;
      rafRef.current = done ? null : requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
  }, [parallaxRef]);

  const setTarget = useCallback((x, y) => {
    targetRef.current = { x, y };
    startLoop();
  }, [startLoop]);

  useEffect(() => () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); }, []);

  return setTarget;
}

// ─── component ────────────────────────────────────────────────────────────────
/**
 * Reusable hero section with animated grid background.
 * Brand colors: primary.main (#9FE870), primary.contrastText (#163300).
 *
 * Props:
 *   chip      – badge label (optional)
 *   children  – heading, subtitle, CTAs
 *   minHeight – default '420px'
 *   py        – vertical padding
 */
const HeroSection = ({ chip, children, minHeight = '420px', py = { xs: 8, md: 14 } }) => {
  const canvasRef   = useRef(null);
  const mouseRef    = useRef({ x: null, y: null });
  const wrapRef     = useRef(null);
  const parallaxRef = useRef(null);

  const setParallax = useSmoothParallax(parallaxRef);

  const onMouseMove = useCallback((e) => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    mouseRef.current = { x, y };
    setParallax(
      (x / rect.width  - 0.5) * 10,
      (y / rect.height - 0.5) *  6,
    );
  }, [setParallax]);

  const onMouseLeave = useCallback(() => {
    mouseRef.current = { x: null, y: null };
    setParallax(0, 0);
  }, [setParallax]);

  useGridCanvas(canvasRef, mouseRef);

  return (
    <Box
      ref={wrapRef}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      sx={{
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        py,
        position: 'relative',
        overflow: 'hidden',
        minHeight,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      {/* animated grid canvas */}
      <Box
        ref={parallaxRef}
        aria-hidden
        sx={{
          position: 'absolute',
          inset: '-12px',
          pointerEvents: 'none',
          willChange: 'transform',
        }}
      >
        <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block' }} />
      </Box>

      {/* radial depth vignette */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(22,51,0,0.10) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      {/* top-right orb */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: '-100px', right: '-100px',
          width: '420px', height: '420px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(22,51,0,0.14) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      {/* bottom-left orb */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          bottom: '-70px', left: '-70px',
          width: '300px', height: '300px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(22,51,0,0.10) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      {/* top shimmer */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(180deg, rgba(255,255,255,0.035) 0%, transparent 55%)',
          pointerEvents: 'none',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, width: '100%' }}>
        {chip && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Chip
              label={chip}
              sx={{
                bgcolor: 'rgba(22,51,0,0.13)',
                color: 'primary.contrastText',
                fontWeight: 700,
                fontSize: '0.8rem',
                letterSpacing: '0.04em',
                border: '1px solid rgba(22,51,0,0.22)',
                backdropFilter: 'blur(4px)',
              }}
            />
          </Box>
        )}
        {children}
      </Container>
    </Box>
  );
};

export default HeroSection;
